import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  PolarAngleAxis,
  RadialBar,
  RadialBarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type {
  AccountWithBuckets,
  CategorySpend,
  MonthlyFlow,
  NetWorthResponse,
  SavingsGoalView,
  WaterfallStep,
} from '@financial/shared';
import { AccountType } from '@financial/shared';
import { ACCOUNT_TYPE_META } from '../../lib/accountTypes';
import { formatMoney } from '../../lib/format';

const INCOME = '#15803d';
const EXPENSE = '#b91c1c';
const SAVINGS = '#2563eb';

const MONTH_ABBR = [
  'Ene',
  'Feb',
  'Mar',
  'Abr',
  'May',
  'Jun',
  'Jul',
  'Ago',
  'Sep',
  'Oct',
  'Nov',
  'Dic',
];

function shortMoney(v: number): string {
  const abs = Math.abs(v);
  if (abs >= 1000) return `${v < 0 ? '-' : ''}$${Math.round(abs / 1000)}k`;
  return `${v < 0 ? '-' : ''}$${Math.round(abs)}`;
}

export function ChartCard({
  title,
  description,
  children,
  actions,
  className = '',
  to,
}: {
  title: string;
  description?: string;
  children: ReactNode;
  actions?: ReactNode;
  className?: string;
  /** Destino opcional: sólo se usa en tarjetas sin controles internos. */
  to?: string;
}) {
  const card = (
    <section
      className={`rounded-[16px] border border-slate-200/80 bg-white p-4 shadow-[0_1px_2px_rgba(15,23,42,.06),0_8px_20px_-12px_rgba(15,23,42,.15)] sm:p-5 ${to ? 'transition-all group-hover:-translate-y-0.5 group-hover:border-blue-200 group-hover:shadow-[0_12px_28px_-14px_rgba(37,99,235,.28)]' : className}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-[15px] font-semibold text-slate-900">{title}</h2>
          {description && <p className="mt-1 text-xs leading-4 text-slate-500">{description}</p>}
        </div>
        {actions}
      </div>
      {children}
    </section>
  );
  if (!to) return card;

  return (
    <Link
      to={to}
      className={`group block rounded-[16px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 ${className}`}
      aria-label={`${title}. Abrir detalle.`}
    >
      {card}
    </Link>
  );
}

/** 2 — Cascada del dinero del mes (waterfall). */
export function WaterfallChart({ steps }: { steps: WaterfallStep[] }) {
  let running = 0;
  const data = steps.map((s) => {
    if (s.type === 'start' || s.type === 'total') {
      const row = {
        name: s.label,
        base: 0,
        delta: s.value,
        fill: s.value >= 0 ? SAVINGS : EXPENSE,
      };
      if (s.type === 'start') running = s.value;
      return row;
    }
    // decrease/increase relativo al running
    const base = s.value < 0 ? running + s.value : running;
    const delta = Math.abs(s.value);
    const fill = s.value < 0 ? EXPENSE : INCOME;
    running += s.value;
    return { name: s.label, base, delta, fill };
  });

  return (
    <div className="h-64 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
          <XAxis
            dataKey="name"
            tick={{ fontSize: 10 }}
            interval={0}
            angle={-20}
            textAnchor="end"
            height={50}
          />
          <YAxis tickFormatter={shortMoney} tick={{ fontSize: 11 }} width={44} />
          <Tooltip formatter={(v: number) => formatMoney(v)} />
          <Bar dataKey="base" stackId="w" fill="transparent" />
          <Bar dataKey="delta" stackId="w" radius={[4, 4, 0, 0]}>
            {data.map((d, i) => (
              <Cell key={i} fill={d.fill} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

/** 3 — Medidor gasto vs. límite (bullet/gauge horizontal con zonas). */
export function BudgetGauge({
  spent,
  limit,
  status,
}: {
  spent: number;
  limit: number;
  status: 'ok' | 'warning' | 'over';
}) {
  const color = status === 'over' ? EXPENSE : status === 'warning' ? '#b45309' : INCOME;
  const usedPct = limit > 0 ? Math.round((spent / limit) * 100) : spent > 0 ? 100 : 0;
  const remaining = limit - spent;
  // Cuando se rebasa el límite, la escala crece con el gasto real y deja una
  // marca donde estaba el tope. Así no parece que el indicador se "congeló"
  // en 100% justo cuando más importa distinguir el exceso.
  const visualScale = status === 'over' ? Math.max(spent, 1) : Math.max(limit, 1);
  const fillPct = limit > 0 ? Math.min(100, (spent / visualScale) * 100) : spent > 0 ? 100 : 0;
  const limitMarkerPct =
    status === 'over' && spent > 0 ? Math.min(100, (limit / spent) * 100) : 100;
  return (
    <div>
      <div className="flex items-baseline justify-between">
        <span className="text-2xl font-bold text-slate-900">{formatMoney(spent)}</span>
        <span className="text-sm text-slate-600">de {formatMoney(limit)}</span>
      </div>
      <div
        className="relative mt-3 h-4 w-full overflow-hidden rounded-full"
        aria-label={`Gasto variable: ${formatMoney(spent)} de ${formatMoney(limit)}`}
      >
        {status === 'over' ? (
          <div className="absolute inset-0 bg-red-100" />
        ) : (
          <div className="absolute inset-0 flex" aria-hidden="true">
            <div className="h-full" style={{ width: '70%', backgroundColor: '#dcfce7' }} />
            <div className="h-full" style={{ width: '15%', backgroundColor: '#fef3c7' }} />
            <div className="h-full" style={{ width: '15%', backgroundColor: '#fee2e2' }} />
          </div>
        )}
        <div
          className="absolute inset-y-0 left-0 rounded-full transition-all"
          style={{ width: `${fillPct}%`, backgroundColor: color }}
        />
        {status === 'over' && (
          <div
            className="absolute inset-y-0 z-10 w-0.5 bg-slate-900/75"
            style={{ left: `calc(${limitMarkerPct}% - 1px)` }}
            title={`Tu límite: ${formatMoney(limit)}`}
          />
        )}
      </div>
      {limit <= 0 ? (
        <p className="mt-2 text-sm font-semibold text-slate-600">
          Configura un límite para tener una referencia.
        </p>
      ) : remaining >= 0 ? (
        <p className="mt-2 text-sm font-semibold text-income">Te quedan {formatMoney(remaining)}</p>
      ) : (
        <p className="mt-2 text-sm font-semibold text-expense">
          Te excediste por {formatMoney(Math.abs(remaining))}
        </p>
      )}
      <p className="mt-0.5 text-xs text-slate-500">
        {status === 'over'
          ? `Límite marcado en la barra · ${usedPct}% usado`
          : `${usedPct}% de tu límite usado`}
      </p>
    </div>
  );
}

/**
 * Las barras hacen comparables los montos y, cuando existe, el límite de cada
 * categoría. Una dona era vistosa, pero no respondía la pregunta útil: qué
 * rubro ya no tiene margen para gastar.
 */
export function CategoryBudgetBars({
  data,
  limits,
}: {
  data: CategorySpend[];
  limits: Record<string, number>;
}) {
  const allRows = data.filter((d) => d.total > 0);
  const adjustments = allRows.filter((d) => d.name.trim().toLowerCase() === 'ajustes gastos');
  const actionableRows = allRows.filter((d) => d.name.trim().toLowerCase() !== 'ajustes gastos');
  const rows = actionableRows.slice(0, 6);
  const adjustmentsTotal = adjustments.reduce((sum, row) => sum + row.total, 0);
  if (rows.length === 0 && adjustmentsTotal === 0)
    return <Empty>Sin gasto por categoría este mes.</Empty>;
  return (
    <div className="space-y-3">
      {rows.length > 0 ? (
        <ul className="grid gap-x-6 gap-y-3 md:grid-cols-2 xl:grid-cols-3">
          {rows.map((d) => {
            const limit = d.categoryId ? limits[d.categoryId] : undefined;
            const hasLimit = limit !== undefined && limit > 0;
            const exceeded = hasLimit && d.total > limit;
            // Cuando se excede, el indicador sigue mostrando el gasto completo
            // y conserva una marca para el tope original.
            const scale = hasLimit ? Math.max(limit, d.total) : d.total;
            const usedPct = scale > 0 ? (d.total / scale) * 100 : 0;
            const limitPct = hasLimit && scale > 0 ? (limit / scale) * 100 : 0;
            const remaining = hasLimit ? limit - d.total : undefined;
            return (
              <li key={d.categoryId ?? 'uncategorized'} className="min-w-0">
                <div className="flex items-baseline justify-between gap-3 text-sm">
                  <span className="min-w-0 truncate font-medium text-slate-700">
                    {d.icon ? `${d.icon} ` : ''}
                    {d.name}
                  </span>
                  <span className="shrink-0 text-right tabular-nums">
                    <strong className={exceeded ? 'text-expense' : 'text-slate-900'}>
                      {formatMoney(d.total)}
                    </strong>
                    {hasLimit ? (
                      <span className="text-xs text-slate-600"> de {formatMoney(limit)}</span>
                    ) : (
                      <span className="text-xs text-amber-700"> · sin límite</span>
                    )}
                  </span>
                </div>
                <div className="relative mt-1.5 h-2 overflow-hidden rounded-full bg-slate-100">
                  <div
                    className={`h-full rounded-full ${exceeded ? 'bg-expense' : hasLimit ? 'bg-savings' : 'bg-slate-400'}`}
                    style={{ width: `${usedPct}%` }}
                  />
                  {hasLimit && exceeded && (
                    <span
                      className="absolute inset-y-0 w-0.5 bg-slate-900/70"
                      style={{ left: `calc(${limitPct}% - 1px)` }}
                      aria-label={`Límite de ${formatMoney(limit)}`}
                    />
                  )}
                </div>
                <p
                  className={`mt-1 text-[11px] ${exceeded ? 'font-semibold text-expense' : 'text-slate-500'}`}
                >
                  {remaining === undefined
                    ? 'Asigna un límite si quieres controlar este rubro.'
                    : remaining >= 0
                      ? `Te quedan ${formatMoney(remaining)}.`
                      : `Excediste el límite por ${formatMoney(Math.abs(remaining))}.`}
                </p>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="text-sm text-slate-500">No hay gastos accionables clasificados todavía.</p>
      )}
      {adjustmentsTotal > 0 && (
        <aside
          className="rounded-xl border border-amber-200 bg-amber-50/70 px-3 py-2.5"
          aria-label="Ajustes por revisar"
        >
          <div className="flex items-center justify-between gap-3">
            <p className="text-xs font-semibold text-amber-900">Ajustes por revisar</p>
            <p className="shrink-0 text-sm font-bold tabular-nums text-amber-900">
              {formatMoney(adjustmentsTotal)}
            </p>
          </div>
          <p className="mt-1 text-[11px] leading-4 text-amber-800">
            Se muestran aparte porque conviene identificar y clasificar estos gastos antes de
            tomar decisiones por categoría.
          </p>
        </aside>
      )}
    </div>
  );
}

export type FinancialTrendMode = 'monthly' | 'cumulative';

/** Tendencia única: flujo mensual o resultado acumulado, no dos gráficas redundantes. */
export function FinancialTrendChart({
  months,
  mode,
}: {
  months: MonthlyFlow[];
  mode: FinancialTrendMode;
}) {
  // Ocultar meses sin actividad evita que dos puntos recientes parezcan una
  // curva anual consolidada. La UI acompaña la serie corta con ese contexto.
  const activeMonths = months.filter((m) => m.income !== 0 || m.expense !== 0 || m.net !== 0);
  const series = activeMonths.length > 0 ? activeMonths : months;
  const monthlyData = series.map((m) => ({
    mes: MONTH_ABBR[parseInt(m.month.slice(5), 10) - 1],
    Ingresos: m.income,
    Gastos: m.expense,
  }));
  let accumulated = 0;
  const cumulativeData = series.map((m) => {
    accumulated += m.net;
    return { mes: MONTH_ABBR[parseInt(m.month.slice(5), 10) - 1], Acumulado: accumulated };
  });

  if (mode === 'cumulative') {
    return (
      <div className="h-64 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={cumulativeData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="mes" tick={{ fontSize: 11 }} />
            <YAxis tickFormatter={shortMoney} tick={{ fontSize: 11 }} width={44} />
            <Tooltip formatter={(v: number) => formatMoney(v)} />
            <Line
              type="linear"
              dataKey="Acumulado"
              stroke={SAVINGS}
              strokeWidth={2}
              dot={{ r: 3 }}
              activeDot={{ r: 5 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    );
  }

  return (
    <div className="h-64 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={monthlyData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
          <XAxis dataKey="mes" tick={{ fontSize: 11 }} />
          <YAxis tickFormatter={shortMoney} tick={{ fontSize: 11 }} width={44} />
          <Tooltip formatter={(v: number) => formatMoney(v)} />
          <Bar dataKey="Ingresos" fill={INCOME} radius={[3, 3, 0, 0]} />
          <Bar dataKey="Gastos" fill={EXPENSE} radius={[3, 3, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

/** 7 — Avance meta (barra radial). */
export function GoalRadial({ goal }: { goal: SavingsGoalView }) {
  const pct = Math.round(goal.progress * 100);
  const data = [{ name: goal.name, value: pct, fill: INCOME }];
  return (
    <div className="flex flex-col items-center">
      <div
        className="relative h-40 w-40"
        role="img"
        aria-label={`${goal.name}: ${pct}% completado`}
      >
        <ResponsiveContainer width="100%" height="100%">
          <RadialBarChart
            innerRadius="72%"
            outerRadius="100%"
            data={data}
            startAngle={90}
            endAngle={-270}
          >
            <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
            <RadialBar dataKey="value" cornerRadius={8} background />
          </RadialBarChart>
        </ResponsiveContainer>
        <div
          className="pointer-events-none absolute inset-0 flex items-center justify-center"
          aria-hidden="true"
        >
          <span className="text-2xl font-bold text-slate-900">{pct}%</span>
        </div>
      </div>
      <p className="mt-1 text-center text-xs text-slate-500">
        {formatMoney(goal.saved)} de {formatMoney(goal.targetAmount)} · ahorra{' '}
        {formatMoney(goal.suggestedMonthly)}/mes
      </p>
    </div>
  );
}

/** Distribución de activos por tipo de cuenta; las deudas se muestran aparte. */
export function NetWorthDonut({
  netWorth,
  accounts,
}: {
  netWorth: NetWorthResponse;
  accounts: AccountWithBuckets[];
}) {
  const rows = netWorth.byType
    .filter((t) => t.total !== 0)
    .map((t) => ({
      name: ACCOUNT_TYPE_META[t.type].label,
      value: t.total,
      color: ACCOUNT_TYPE_META[t.type].color,
    }));
  const accountAssets = rows.reduce((total, row) => total + row.value, 0);
  const receivableAssets = netWorth.receivables.reduce(
    (total, receivable) => total + receivable.total,
    0,
  );
  const assets = accountAssets + receivableAssets;
  const liquidTypes = new Set([AccountType.BANK, AccountType.SAVINGS, AccountType.CASH]);
  const liquidAssets = accounts
    .filter((account) => account.includeInNetWorth && liquidTypes.has(account.type))
    .reduce((total, account) => total + account.balance, 0);
  const investedOrRestricted = Math.max(0, accountAssets - liquidAssets);
  if (rows.length === 0 && netWorth.receivables.length === 0)
    return <Empty>Aún no hay cuentas ni préstamos por cobrar.</Empty>;
  return (
    <div className="space-y-4">
      <dl className="grid divide-y divide-slate-200 overflow-hidden rounded-xl border border-slate-200 bg-slate-50/70 sm:grid-cols-3 sm:divide-x sm:divide-y-0">
        <div className="min-w-0 px-3 py-2.5">
          <dt className="text-[10px] font-bold uppercase tracking-[.06em] text-slate-500">
            Total que tienes
          </dt>
          <dd className="mt-1 text-sm font-bold tabular-nums text-slate-900">
            {formatMoney(assets)}
          </dd>
        </div>
        <div className="min-w-0 px-3 py-2.5">
          <dt className="text-[10px] font-bold uppercase tracking-[.06em] text-slate-500">
            Lo que debes
          </dt>
          <dd className="mt-1 text-sm font-bold tabular-nums text-expense">
            −{formatMoney(netWorth.liabilities)}
          </dd>
        </div>
        <div className="min-w-0 px-3 py-2.5">
          <dt className="text-[10px] font-bold uppercase tracking-[.06em] text-slate-500">
            Diferencia
          </dt>
          <dd
            className={`mt-1 text-sm font-bold tabular-nums ${netWorth.total >= 0 ? 'text-income' : 'text-expense'}`}
          >
            {formatMoney(netWorth.total)}
          </dd>
        </div>
      </dl>
      <div className="grid gap-2 sm:grid-cols-2">
        <div className="rounded-xl border border-emerald-100 bg-emerald-50/60 px-3 py-2.5">
          <p className="text-[10px] font-bold uppercase tracking-[.06em] text-emerald-700">
            Disponible de inmediato
          </p>
          <p className="mt-1 text-sm font-bold tabular-nums text-emerald-900">
            {formatMoney(liquidAssets)}
          </p>
          <p className="mt-0.5 text-[11px] leading-4 text-emerald-800">
            Banco, ahorro y efectivo; antes de descontar apartados.
          </p>
        </div>
        <div className="rounded-xl border border-violet-100 bg-violet-50/60 px-3 py-2.5">
          <p className="text-[10px] font-bold uppercase tracking-[.06em] text-violet-700">
            Ahorro o inversión
          </p>
          <p className="mt-1 text-sm font-bold tabular-nums text-violet-900">
            {formatMoney(investedOrRestricted)}
          </p>
          <p className="mt-0.5 text-[11px] leading-4 text-violet-800">
            CETES y caja de ahorro: no se asumen disponibles para gasto diario.
          </p>
        </div>
      </div>
      {rows.length > 0 ? (
        <div className="flex flex-col items-center gap-3 sm:flex-row">
          <div className="h-44 w-full sm:w-1/2">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={rows}
                  dataKey="value"
                  nameKey="name"
                  innerRadius="55%"
                  outerRadius="85%"
                  paddingAngle={2}
                >
                  {rows.map((r) => (
                    <Cell key={r.name} fill={r.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(v: number) => formatMoney(v)} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <ul className="w-full space-y-1 text-sm sm:w-1/2">
            {rows.map((r) => (
              <li key={r.name} className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <span
                    className="inline-block h-3 w-3 rounded-full"
                    style={{ backgroundColor: r.color }}
                  />
                  {r.name}
                </span>
                <span className="font-medium text-slate-700">{formatMoney(r.value)}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <p className="text-sm text-slate-500">
          No hay saldos de cuenta incluidos en el total de lo que tienes.
        </p>
      )}
      <p className="text-[11px] leading-4 text-slate-500">
        La gráfica sólo distribuye el dinero de tus cuentas; lo que debes se resta en el cálculo de
        arriba.
      </p>

      {netWorth.receivables.length > 0 && (
        <div className="rounded-xl border border-emerald-100 bg-emerald-50/60 px-3 py-2.5">
          <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">
            Préstamos por cobrar
          </p>
          <p className="mt-0.5 text-xs leading-4 text-emerald-800">
            Son parte del total de lo que tienes, pero no son dinero disponible hasta que los cobres y los registres
            en una cuenta.
          </p>
          <ul className="mt-2 space-y-1.5 text-sm">
            {netWorth.receivables.map((receivable) => (
              <li key={receivable.label} className="flex items-center justify-between gap-3">
                <span className="min-w-0 truncate text-emerald-950">{receivable.label}</span>
                <span className="shrink-0 font-semibold tabular-nums text-emerald-800">
                  {formatMoney(receivable.total)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function Empty({ children }: { children: ReactNode }) {
  return <p className="py-8 text-center text-sm text-slate-600">{children}</p>;
}
